import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollEngine } from '@/utils/payrollEngine';
import { calculatePayroll, type PayrollCalculationInput, type PayrollCalculationResult } from '@/utils/payrollCalculations';
import { useToast } from '@/hooks/use-toast';

export function usePayrollCalculations(payrollPeriodId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: calculations, isLoading } = useQuery({
    queryKey: ['payroll-calculations', payrollPeriodId],
    queryFn: () => payrollPeriodId ? PayrollEngine.getPayrollCalculationsForPeriod(payrollPeriodId) : Promise.resolve([]),
    enabled: !!payrollPeriodId,
  });

  const calculateBulkPayrollMutation = useMutation({
    mutationFn: (periodId: string) => PayrollEngine.calculateBulkPayroll(periodId),
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ['payroll-calculations'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-periods'] });
      
      toast({
        title: 'Bulk Payroll Calculation Complete',
        description: `Successfully calculated ${results.success.length} payrolls. ${results.errors.length} errors occurred.`,
        variant: results.errors.length > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error) => {
      toast({
        title: 'Bulk Calculation Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const approvePayrollMutation = useMutation({
    mutationFn: ({ periodId, approvedBy }: { periodId: string; approvedBy: string }) =>
      PayrollEngine.approvePayrollPeriod(periodId, approvedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-periods'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-calculations'] });
      
      toast({
        title: 'Payroll Approved',
        description: 'The payroll period has been successfully approved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Approval Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  return {
    calculations,
    isLoading,
    calculateBulkPayroll: calculateBulkPayrollMutation.mutate,
    isCalculating: calculateBulkPayrollMutation.isPending,
    approvePayroll: approvePayrollMutation.mutate,
    isApproving: approvePayrollMutation.isPending,
  };
}

export function usePayrollCalculator() {
  const [calculation, setCalculation] = useState<PayrollCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const { toast } = useToast();

  const calculateSinglePayroll = async (input: PayrollCalculationInput) => {
    setIsCalculating(true);
    try {
      const result = calculatePayroll(input);
      setCalculation(result);
      
      toast({
        title: 'Calculation Complete',
        description: `Gross: CHF ${result.gross_salary.toFixed(2)}, Net: CHF ${result.net_salary.toFixed(2)}`,
      });
    } catch (error) {
      toast({
        title: 'Calculation Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const resetCalculation = () => {
    setCalculation(null);
  };

  return {
    calculation,
    isCalculating,
    calculateSinglePayroll,
    resetCalculation,
  };
}